import React, { useState, useEffect, useRef } from 'react';
import { Search, Users, ChevronRight, Building2, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import useIndustryProfilesList from '../../hooks/useIndustryProfilesList';
import DataModeBadge from './DataModeBadge';
import api from '../../lib/api';

const REGION_LABELS = {
  north_america: 'North America', europe: 'Europe', asia_pacific: 'Asia Pacific',
  latin_america: 'Latin America', middle_east: 'Middle East', africa: 'Africa', global: 'Global'
};

// Friendly-name fallback for industries an org might still reference
// that no longer exist in the published library (e.g. archived,
// renamed, or legacy slugs from before the Industry Library shipped).
function prettifySlug(slug) {
  if (!slug) return '';
  return slug
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

export const OrgListView = ({ organizations, setManagingOrgId, pulsingOrgId }) => {
  const [nameFilter, setNameFilter] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const { byId: industryById } = useIndustryProfilesList();
  const [dataModes, setDataModes] = useState({});
  const [modeFilter, setModeFilter] = useState('all');
  useEffect(() => {
    api.get('/admin/benchmark-baseline/data-modes')
      .then((res) => setDataModes(res?.data?.modes || {}))
      .catch(() => setDataModes({}));
  }, []);
  // Scroll the pulsing org card into view so the highlight isn't lost
  // below the fold when an admin lands here from the breadcrumb.
  const pulsingCardRef = useRef(null);
  useEffect(() => {
    if (pulsingOrgId && pulsingCardRef.current) {
      pulsingCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [pulsingOrgId]);

  const distinctIndustries = [...new Set(organizations.map(o => o.industry_type).filter(Boolean))];
  const distinctRegions = [...new Set(organizations.map(o => o.region).filter(Boolean))];

  const filtered = organizations.filter(org => {
    if (nameFilter && !org.name.toLowerCase().includes(nameFilter.toLowerCase())) return false;
    if (industryFilter !== 'all' && (org.industry_type || '') !== industryFilter) return false;
    if (regionFilter !== 'all' && (org.region || '') !== regionFilter) return false;
    if (modeFilter !== 'all') {
      const m = dataModes[org.id]?.mode;
      if (modeFilter === 'benchmarks' && !['modeled', 'mixed'].includes(m)) return false;
      if (['modeled', 'mixed', 'measured'].includes(modeFilter) && m !== modeFilter) return false;
    }
    return true;
  });

  const benchmarkCount = organizations.filter(
    (o) => ['modeled', 'mixed'].includes(dataModes[o.id]?.mode)).length;

  const hasActiveFilters = nameFilter || industryFilter !== 'all' || regionFilter !== 'all' || modeFilter !== 'all';

  return (
    <Card data-testid="org-list-view">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Organizations</CardTitle>
            <CardDescription>
              {filtered.length} of {organizations.length} organization{organizations.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>
          {benchmarkCount > 0 && (
            <button
              onClick={() => setModeFilter(modeFilter === 'benchmarks' ? 'all' : 'benchmarks')}
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                modeFilter === 'benchmarks'
                  ? 'bg-cyan-600 text-white border-cyan-600'
                  : 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100'
              }`}
              data-testid="org-benchmark-triage-chip"
              title="Show only organizations still running on modeled benchmarks"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {benchmarkCount} org{benchmarkCount !== 1 ? 's' : ''} still on benchmarks
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-3" data-testid="org-filter-bar">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="org-filter-name"
            />
          </div>
          {distinctIndustries.length > 0 && (
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="org-filter-industry">
                <SelectValue placeholder="All Industries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {distinctIndustries.map(ind => (
                  <SelectItem key={ind} value={ind}>{industryById[ind]?.name || prettifySlug(ind)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {distinctRegions.length > 0 && (
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="org-filter-region">
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {distinctRegions.map(reg => (
                  <SelectItem key={reg} value={reg}>{REGION_LABELS[reg] || reg}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={modeFilter} onValueChange={setModeFilter}>
            <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="org-filter-mode">
              <SelectValue placeholder="All Data Modes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Data Modes</SelectItem>
              <SelectItem value="benchmarks">On benchmarks</SelectItem>
              <SelectItem value="modeled">Modeled</SelectItem>
              <SelectItem value="mixed">Mixed</SelectItem>
              <SelectItem value="measured">Measured</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-gray-500 h-9"
              onClick={() => { setNameFilter(''); setIndustryFilter('all'); setRegionFilter('all'); setModeFilter('all'); }}
              data-testid="org-filter-clear"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            {hasActiveFilters ? 'No organizations match your filters.' : 'No organizations found.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map(org => {
              const isPulsing = pulsingOrgId && pulsingOrgId === org.id;
              return (
              <Card
                key={org.id}
                ref={isPulsing ? pulsingCardRef : null}
                data-testid={`org-card-${org.id}`}
                data-pulsing={isPulsing ? 'true' : undefined}
                className={`border transition-all duration-500 ${
                  isPulsing
                    ? 'ring-2 ring-amber-400 ring-offset-2 shadow-lg animate-pulse bg-amber-50/40'
                    : ''
                }`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                        <Building2 className="text-teal-600" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{org.name}</h3>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {org.industry_type && (
                            <Badge variant="outline" className="text-xs font-normal">
                              {industryById[org.industry_type]?.name || prettifySlug(org.industry_type)}
                            </Badge>
                          )}
                          {org.region && (
                            <Badge variant="outline" className="text-xs font-normal text-blue-600 border-blue-200">
                              {REGION_LABELS[org.region] || org.region}
                            </Badge>
                          )}
                          <DataModeBadge mode={dataModes[org.id]?.mode} testId={`org-data-mode-${org.id}`} />
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline">{org.subscription_tier || 'free'}</Badge>
                  </div>
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {org.user_count || 0} users
                      </span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setManagingOrgId(org.id)} data-testid={`manage-org-${org.id}`}>
                      Manage <ChevronRight size={14} className="ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
